import PropTypes from 'prop-types';

import { QUESTION_TYPES, QUESTION_TYPES_WITH_SUBQUESTIONS } from '@api/instant_survey';
import { IconButton, ResetIcon } from '@utilities';

import { isLanguageScreenerQuestion } from '../../../../../../utilities/helpers';
import TranslationRow from '../TranslationRow';

import styles from '../../_index.module.scss';
import rowStyles from '../TranslationRow/_index.module.scss';

const QUESTION_TYPE_NAMES = Object.values(QUESTION_TYPES).reduce(
  (names, { name, value }) => ({ ...names, [value]: name }),
  {}
);

const getQuestionTypeName = (type) => (QUESTION_TYPE_NAMES[type] || type || '').toUpperCase();
const isInstructionQuestion = (question) => question?.type === QUESTION_TYPES.INSTRUCTIONS.value;
const isScreenerQuestion = (question) =>
  question?.isScreener || isLanguageScreenerQuestion(question);
const getQuestionDisplayLabel = (question, index) => {
  if (isInstructionQuestion(question)) return 'T';
  if (isScreenerQuestion(question)) return `S${index + 1}`;
  return `Q${index + 1}`;
};

const isMaxDiffQuestion = (question) => question.type === QUESTION_TYPES.MAX_DIFF.value;

const QuestionTranslations = ({
  appliedLanguages,
  getEntry,
  getTranslation,
  hasConceptName,
  index,
  isDisabled,
  languageLabels,
  onClear,
  onDismissRelevanceWarning,
  onPrioritizeQuestion,
  onReset,
  onTranslationChange,
  question,
  registerQuestion,
  sourceLanguage,
}) => {
  const questionDisplayLabel = getQuestionDisplayLabel(question, index);

  return (
    <div
      className={styles['modal-translation-section']}
      ref={registerQuestion ? registerQuestion(question.id) : undefined}
    >
      <div className={styles['modal-translation-section-header']}>
        <h3>{`${questionDisplayLabel} · ${getQuestionTypeName(question.type)}`}</h3>
        {appliedLanguages.length > 0 && !isLanguageScreenerQuestion(question) && (
          <IconButton
            aria-label={`Reset ${questionDisplayLabel} translations`}
            isDisabled={isDisabled}
            onClick={() => onReset(question.id)}
            tooltip="Reset"
            type="button"
          >
            <ResetIcon />
          </IconButton>
        )}
      </div>
      <TranslationRow
        appliedLanguages={appliedLanguages}
        entityKey={`question-${question.id}`}
        getEntry={getEntry}
        getTranslation={getTranslation}
        hasConceptName={hasConceptName}
        isDisabled={isDisabled || isLanguageScreenerQuestion(question)}
        languageLabels={languageLabels}
        onClear={() => onClear(`question-${question.id}`)}
        onDismissRelevanceWarning={onDismissRelevanceWarning}
        onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
        onTranslationChange={onTranslationChange}
        sourceLanguage={sourceLanguage}
        sourceRichText={question.richText || question.text}
        text={question.text}
      />
      {isMaxDiffQuestion(question) && question.properties?.bestLabel && (
        <TranslationRow
          appliedLanguages={appliedLanguages}
          entityKey={`maxdiff-best-label-${question.id}`}
          getEntry={getEntry}
          getTranslation={getTranslation}
          hasConceptName={hasConceptName}
          isDisabled={isDisabled}
          languageLabels={languageLabels}
          onClear={() => onClear(`maxdiff-best-label-${question.id}`)}
          onDismissRelevanceWarning={onDismissRelevanceWarning}
          onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
          onTranslationChange={onTranslationChange}
          sourceLanguage={sourceLanguage}
          sourceRichText={question.properties.bestLabel}
          text={`Best label: ${question.properties.bestLabel}`}
        />
      )}
      {isMaxDiffQuestion(question) && question.properties?.worstLabel && (
        <TranslationRow
          appliedLanguages={appliedLanguages}
          entityKey={`maxdiff-worst-label-${question.id}`}
          getEntry={getEntry}
          getTranslation={getTranslation}
          hasConceptName={hasConceptName}
          isDisabled={isDisabled}
          languageLabels={languageLabels}
          onClear={() => onClear(`maxdiff-worst-label-${question.id}`)}
          onDismissRelevanceWarning={onDismissRelevanceWarning}
          onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
          onTranslationChange={onTranslationChange}
          sourceLanguage={sourceLanguage}
          sourceRichText={question.properties.worstLabel}
          text={`Worst label: ${question.properties.worstLabel}`}
        />
      )}
      {isLanguageScreenerQuestion(question)
        ? question.answers
            ?.filter((answer) => !answer._delete)
            .map((answer) => (
              <div
                className={`${rowStyles['modal-translation-row']} ${rowStyles['modal-translation-row-readonly']}`}
                key={answer.id}
              >
                <span className={rowStyles['modal-translation-row-source']}>{answer.text}</span>
              </div>
            ))
        : question.answers
            ?.filter((answer) => !answer._delete)
            .map((answer) => (
              <TranslationRow
                appliedLanguages={appliedLanguages}
                entityKey={`option-${question.id}-${answer.id}`}
                getEntry={getEntry}
                getTranslation={getTranslation}
                hasConceptName={hasConceptName}
                isDisabled={isDisabled}
                key={answer.id}
                languageLabels={languageLabels}
                onClear={() => onClear(`option-${question.id}-${answer.id}`)}
                onDismissRelevanceWarning={onDismissRelevanceWarning}
                onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
                onTranslationChange={onTranslationChange}
                sourceLanguage={sourceLanguage}
                sourceRichText={answer.richText || answer.text}
                text={answer.text}
              />
            ))}
      {QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type) &&
        question.subQuestions
          ?.filter((subQuestion) => !subQuestion._delete)
          .map((subQuestion) => (
            <TranslationRow
              appliedLanguages={appliedLanguages}
              entityKey={`subquestion-${question.id}-${subQuestion.id}`}
              getEntry={getEntry}
              getTranslation={getTranslation}
              hasConceptName={hasConceptName}
              isDisabled={isDisabled}
              key={subQuestion.id}
              languageLabels={languageLabels}
              onClear={() => onClear(`subquestion-${question.id}-${subQuestion.id}`)}
              onDismissRelevanceWarning={onDismissRelevanceWarning}
              onPrioritizeTranslation={() => onPrioritizeQuestion?.(question.id)}
              onTranslationChange={onTranslationChange}
              sourceLanguage={sourceLanguage}
              sourceRichText={subQuestion.richText || subQuestion.text}
              text={subQuestion.text}
            />
          ))}
    </div>
  );
};

QuestionTranslations.propTypes = {
  appliedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  getEntry: PropTypes.func.isRequired,
  getTranslation: PropTypes.func.isRequired,
  hasConceptName: PropTypes.bool,
  index: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool,
  languageLabels: PropTypes.object.isRequired,
  onClear: PropTypes.func.isRequired,
  onDismissRelevanceWarning: PropTypes.func.isRequired,
  onPrioritizeQuestion: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onTranslationChange: PropTypes.func.isRequired,
  question: PropTypes.object.isRequired,
  registerQuestion: PropTypes.func,
  sourceLanguage: PropTypes.string,
};

export default QuestionTranslations;
